<div mat-dialog-title fxLayoutAlign="space-between center">
  <h2>
    {{ 'dialog.eligibility.pull-eligibility' | translate }}
  </h2>
</div>

<mat-progress-bar *ngIf="progress$ | async" mode="indeterminate"></mat-progress-bar>
<div>
  <form
    fxLayout="column"
    *ngIf="form"
    [formGroup]="form"
    fxLayoutGap="10px"
    (ngSubmit)="submit$.next()"
  >
    <mat-dialog-content
      fxLayout="column"
      fxLayoutGap="1rem"
      style="max-height: 65vh"
    >
      <ng-container *ngIf="data.insurance == null && insurances$ | async as insurances">
        <mat-form-field *ngIf="insurances?.length > 0">
          <mat-select formControlName="insurance">
            <mat-option [value]="null">
              {{ 'dialog.eligibility.new-insurance' | translate }}
            </mat-option>
            <mat-option *ngFor="let insurance of insurances" [value]="insurance">
              {{ insurance.clinicCarrierName }} - {{ insurance.memberId }}
            </mat-option>
          </mat-select>
          <mat-hint>
            {{ 'dialog.eligibility.insurance-hint' | translate }}
          </mat-hint>
        </mat-form-field>
      </ng-container>

      <div *ngIf="data.insurance" fxLayout="row" fxLayoutAlign="space-between center">
        <span>Pulling benefits for {{ data.insurance.clinicCarrierName }}.</span>
        <ng-container *ngIf="!showLookupSelector && !showProviderSelector">
          <button mat-stroked-button type="button" (click)="switchToProviders()">Switch payer</button>
        </ng-container>
      </div>

      <div *ngIf="showLookupSelector" fxLayout="row" fxLayoutGap="1em">
        <mat-form-field fxFlex="grow">
          <mat-label>
            {{ 'dialog.eligibility.insurance-carrier' | translate }}
          </mat-label>
          <input
            matInput
            required
            type="text"
            formControlName="lookup"
            [matAutocomplete]="autoLookup"
          />
          <mat-hint *ngIf="form.value.lookup?.provider">
            {{ 'dialog.eligibility.payer' | translate }}:
            {{ form.value.lookup.provider?.name }},
            {{ 'dialog.eligibility.clearinghouse' | translate }}:
            {{ form.value.lookup.provider?.source | titlecase }}
            ({{ form.value.lookup.provider?.slug }})
          </mat-hint>
          <mat-error *ngIf="form.controls.lookup.invalid">{{
            getErrorMessage('lookup')
          }}</mat-error>
          <mat-autocomplete
            autoActiveFirstOption
            #autoLookup="matAutocomplete"
            [displayWith]="displayAutocompleteLabel.bind(this)"
            (optionSelected)="onLookupSelected($event.option.value)"
          >
            <mat-option
              *ngFor="let lookup of filteredLookups$ | async"
              fxLayout="row"
              [value]="lookup"
              [disabled]="lookup.provider?.enrollmentRequired && !lookup.enrolled"
            >
              <span>{{ lookup.viewValue }}</span>
              <span fxFlex="10px"></span>
              <span *ngIf="lookup.provider?.enrollmentRequired && !lookup.enrolled" class="text-warn">{{
                'dialog.eligibility.enrollment-required' | translate
              }}</span>
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>

        <button
          mat-stroked-button
          type="button"
          (click)="switchToProviders()"
          [matTooltip]="'dialog.eligibility.show-all-insurances' | translate"
        >
          {{ 'common.more' | translate }}
        </button>
      </div>

      <div *ngIf="showProviderSelector" fxLayout="row" fxLayoutGap="1em">
        <mat-form-field fxFlex="grow">
          <mat-label>{{
            'dialog.eligibility.payer' | translate
          }}</mat-label>
          <input matInput required type="text" formControlName="provider" [matAutocomplete]="autoProvider" />
          <mat-hint *ngIf="form.value.provider">
            {{ 'dialog.eligibility.clearinghouse' | translate }}:
            {{ form.value.provider?.source | titlecase }}
            ({{ form.value.provider?.slug }})
          </mat-hint>
          <mat-error *ngIf="form.controls.provider.invalid">{{
            getErrorMessage('provider')
          }}</mat-error>
          <mat-autocomplete autoActiveFirstOption #autoProvider="matAutocomplete" [displayWith]="displayAutocompleteLabel.bind(this)"
            (optionSelected)="onProviderSelected($event.option.value)">
            <mat-option *ngFor="let provider of filteredProviders$ | async" fxLayout="row" [value]="provider" [disabled]="provider.enrollmentRequired">
              <span>{{ provider.viewValue }}</span>
              <span fxFlex="10px"></span>
              <span *ngIf="provider.enrollmentRequired" class="text-warn">{{
                'dialog.eligibility.enrollment-required' | translate
                }}</span>
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>

        <button
          mat-stroked-button
          type="button"
          (click)="switchToLookups()"
          matTooltip="Switch back to selecting carriers found in your EMR"
        >
          {{ 'common.less' | translate }}
        </button>
      </div>

      <div fxFlex *ngIf="showLookupSelector && carrierNotes">
        <strong>{{ 'common.notes' | translate }}:</strong>
        {{ carrierNotes }}
      </div>

      <div fxLayout="row" fxLayoutAlign="space-between">
        <mat-slide-toggle
          formControlName="useDOB"
          (change)="onUseDOBChange()"
          color="primary"
        >
          {{ 'dialog.eligibility.use-dob' | translate }}
        </mat-slide-toggle>

        <div *ngIf="requiresMemberId">
          {{ 'dialog.eligibility.carrier-requires-id' | translate }}.
        </div>
      </div>

      <div
        *ngIf="!form.value.useDOB"
        fxLayout="row"
        fxLayoutGap="10px"
        fxLayoutAlign="space-between center"
      >
        <mat-form-field fxFlex>
          <mat-label>{{
            'dialog.eligibility.member-subscriber-id' | translate
          }}</mat-label>
          <input
            matInput
            required
            type="text"
            formControlName="memberId"
            autocomplete="off"
          />
          <mat-hint>{{
            'dialog.eligibility.member-subscriber-id-hint' | translate
          }}</mat-hint>
          <mat-error *ngIf="form.controls.memberId.invalid">{{
            getErrorMessage('memberId')
          }}</mat-error>
        </mat-form-field>

        <mat-form-field fxFlex>
          <mat-label>{{
            'dialog.eligibility.group-number' | translate
          }}</mat-label>
          <input
            matInput
            type="text"
            formControlName="groupNumber"
            autocomplete="off"
          />
          <mat-hint>{{ 'common.optional' | translate }}.</mat-hint>
          <mat-error *ngIf="form.controls.groupNumber.invalid">{{
            getErrorMessage('groupNumber')
          }}</mat-error>
        </mat-form-field>
      </div>

      <div
        fxLayout="row"
        fxLayoutGap="10px"
        fxLayoutAlign="space-between center"
      >
        <mat-form-field fxFlex>
          <mat-label>{{
            'dialog.eligibility.patient-dob' | translate
          }}</mat-label>
          <mmx-date-selector-input
            formControlName="dob"
            [dob]="true"
            autocomplete="off"
            [required]="true"
          ></mmx-date-selector-input>
          <mat-hint>MM/DD/YYYY</mat-hint>
          <mat-error *ngIf="form.controls.dob.invalid">{{
            getErrorMessage('dob')
          }}</mat-error>
        </mat-form-field>

        <mat-form-field fxFlex>
          <mat-label>{{
            'dialog.eligibility.patient-sex' | translate
          }}</mat-label>
          <mat-select formControlName="sex">
            <mat-option *ngIf="patient?.sex == null" [value]="null">
              {{ 'common.unspecified' | translate }}
            </mat-option>
            <mat-option *ngFor="let option of sexes" [value]="option.value">{{
              option.viewValue | translate
            }}</mat-option>
          </mat-select>
          <!-- <mat-hint>Defaults to patient's sex.</mat-hint> -->
          <mat-error *ngIf="form.controls.sex.invalid">{{
            getErrorMessage('sex')
          }}</mat-error>
        </mat-form-field>
      </div>

      <div class="patient-name">
        <div
          fxLayout="row"
          fxLayoutGap="10px"
          fxLayoutAlign="space-between center"
          formGroupName="patientName"
        >
          <mat-form-field fxFlex>
            <mat-label>{{
              'dialog.eligibility.given-name' | translate
            }}</mat-label>
            <input matInput class="uppercase" type="text" formControlName="given" />
            <mat-hint>{{ 'dialog.eligibility.given-name-hint' | translate }}</mat-hint>
          </mat-form-field>

          <mat-form-field fxFlex>
            <mat-label>{{
              'dialog.eligibility.family-name' | translate
            }}</mat-label>
            <input matInput class="uppercase" type="text" formControlName="family" />
            <mat-hint>{{ 'dialog.eligibility.family-name-hint' | translate }}</mat-hint>
          </mat-form-field>
        </div>
      </div>

      <div fxFlex fxLayout="row">
        <ng-container *ngIf="supportsMultipleServiceTypes; else singleSelect">
          <mat-form-field fxFlex>
            <mat-label>{{ 'dialog.eligibility.service-type' | translate }}</mat-label>
            <mat-select formControlName="serviceTypeCodes" multiple required class="custom-margin">
              <mat-option>
                <ngx-mat-select-search
                  [formControl]="serviceFilterCtrl"
                  placeholderLabel="Find service…"
                  noEntriesFoundLabel="No matching service type found"
                ></ngx-mat-select-search>
              </mat-option>
              <mat-option *ngFor="let serviceType of filteredServiceTypes | async" [value]="serviceType.code">
                [{{ serviceType.code }}] {{ serviceType.name | titlecase }}
              </mat-option>
            </mat-select>
            <mat-hint>{{ 'dialog.eligibility.service-type-hint' | translate }}</mat-hint>
            <mat-error *ngIf="form.get('serviceTypeCodes').hasError('maxLimitReached')">
              {{ form.get('serviceTypeCodes').getError('maxLimitReached').message }}
            </mat-error>
          </mat-form-field>
        </ng-container>
        <ng-template #singleSelect>
          <mat-form-field fxFlex>
            <mat-label>{{ 'dialog.eligibility.service-type' | translate }}</mat-label>
            <mat-select formControlName="serviceTypeCodes" required class="custom-margin">
              <mat-option *ngFor="let serviceType of filteredServiceTypes | async" [value]="serviceType.code">
                [{{ serviceType.code }}] {{ serviceType.name | titlecase }}
              </mat-option>
            </mat-select>
            <mat-hint>{{ 'dialog.eligibility.service-type-hint' | translate }}</mat-hint>
            <mat-error *ngIf="form.get('serviceTypeCodes').hasError('maxLimitReached')">
              {{ form.get('serviceTypeCodes').getError('maxLimitReached').message }}
            </mat-error>
          </mat-form-field>
        </ng-template>
      </div>

      <mat-form-field>
        <mat-label>{{
          'dialog.eligibility.date-of-service' | translate
        }}</mat-label>
        <mmx-date-selector-input formControlName="dateOfService" [dob]="true" autocomplete="off"
          required></mmx-date-selector-input>
        <mat-hint>{{
          'dialog.eligibility.date-of-service-hint' | translate
        }}</mat-hint>
        <mat-error *ngIf="form.controls.dateOfService.invalid">{{
          getErrorMessage('dateOfService')
        }}</mat-error>
      </mat-form-field>
    </mat-dialog-content>

    <mat-error *ngIf="errorMessage" class="p-4">{{
      errorMessage | titlecase
    }}</mat-error>

    <mat-dialog-actions fxLayout="row" fxLayoutAlign="space-between center">
      <button mat-stroked-button type="button" (click)="onCancelClick()">
        {{ 'common.cancel' | translate }}
      </button>
      <button
        mat-flat-button
        type="submit"
        color="primary"
        [disabled]="form.invalid || (progress$ | async)"
      >
        {{ 'common.pull' | translate }}
      </button>
    </mat-dialog-actions>
  </form>
</div>
